// @flow
import React, { Component } from 'react';

import RaisedButton from 'material-ui/RaisedButton';
import { cyan500, cyan700 } from 'material-ui/styles/colors';

import DND_STATES from './states';

import type { Node } from 'react';
import type { Feature2D } from 'flow-geojson';

import FeatureToggle from 'components/FeatureToggle';

const VALID_MIME_TYPES = [
  'application/vnd.google-earth.kmz',
  'application/vnd.google-earth.kml+xml',
];

const getStyles = ({
  browser: {
    lessThan: { medium: isMobile },
  },

  dndState,
}) => ({
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,

    background: 'rgba(255, 255, 255, .75)',
    zIndex: 1399,
    WebkitBackdropFilter: 'blur(5px)',
  },

  overlayContent: {
    textAlign: 'center',
  },

  icon: {
    width: isMobile ? 150 : 250,
  },

  message: {
    display: isMobile && dndState === 'awaitDrop' ? 'none' : 'block',
    fontSize: isMobile ? '1.5em' : '2.5em',
    fontWeight: 'bold',
  },

  orLabel: {
    display: isMobile ? 'none' : 'block',
    marginBottom: '2em',
    fontSize: '.75em',
    color: cyan700,
  },

  fileInput: {
    cursor: 'pointer',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    width: '100%',
    opacity: 0,
  },

  uploadButtonContainer: {
    marginTop: '1em',
  },

  uploadButton: {
    backgroundColor: cyan500,
  },
});

type Props = {
  browser: {
    lessThan: {
      medium: boolean,
    },
  },

  toggleDrawMode: boolean => void,
  drawingFinished: Feature2D => void,
  setFitBounds: (number[]) => void,

  isDragging: boolean,
  dndState: ?$Keys<typeof DND_STATES>,
  buttonVisible: boolean,

  dndOverlaySetIsDragging: boolean => void,
  dndOverlaySetDndState: (?$Keys<typeof DND_STATES> | null) => void,
  dndOverlaySetButtonVisible: boolean => void,

  children: Node,
};

export default class DNDOverlay extends Component<Props> {
  dragTargets = [];
  fileUploadInputRef = null;

  _getTransferItems(e: SyntheticInputEvent<*>): File[] {
    let items = [];

    if (e.dataTransfer) {
      if (e.dataTransfer.files && e.dataTransfer.files.length) {
        items = e.dataTransfer.files;
      } else if (e.dataTransfer.items && e.dataTransfer.items.length) {
        items = e.dataTransfer.items;
      }
    } else if (e.target.files) {
      items = e.target.files;
    }

    return Array.prototype.slice.call(items);
  }

  _handleDragEnter(e: SyntheticInputEvent<*>): void {
    e.stopPropagation();
    e.preventDefault();

    if (!this.dragTargets.includes(e.target)) {
      this.dragTargets.push(e.target);
    }

    if (!this.props.isDragging) {
      const items = this._getTransferItems(e);
      this.props.dndOverlaySetButtonVisible(false);
      this.props.dndOverlaySetIsDragging(false);
      this.props.dndOverlaySetDndState(
        items && items.length && items.length > 1 ? 'multiple' : 'awaitDrop'
      );
    }
  }

  _handleDragLeave(e: SyntheticEvent<*>): void {
    e.stopPropagation();
    e.preventDefault();

    this.dragTargets = this.dragTargets.filter(target => target !== e.target);

    if (this.dragTargets.length === 0) {
      this.props.dndOverlaySetIsDragging(false);
      this.props.dndOverlaySetDndState(null);
    }
  }

  async _handleDrop(e: SyntheticInputEvent<*>): Promise<*> {
    e.stopPropagation();
    e.preventDefault();

    const items = this._getTransferItems(e);

    if (items.length === 0) {
      this.props.dndOverlaySetIsDragging(false);
      this.props.dndOverlaySetDndState(null);
    } else if (items.length > 1) {
      this.props.dndOverlaySetDndState('multiple');
      setTimeout(() => {
        this.props.dndOverlaySetIsDragging(false);
        this.props.dndOverlaySetDndState(null);
      }, 2000);
    } else if (
      !['kml', 'kmz'].includes(items[0].name.toLowerCase().slice(-3))
    ) {
      this.props.dndOverlaySetDndState('unknown');
      setTimeout(() => {
        this.props.dndOverlaySetIsDragging(false);
        this.props.dndOverlaySetDndState(null);
      }, 2000);
    } else {
      this.props.dndOverlaySetDndState('processing');

      try {
        const kmlUtils = await import(/* webpackChunkName: 'kml' */
        'utils/kml');
        const { bbox, feature } = await kmlUtils.parseFeatureFromKML(items[0]);

        this.props.toggleDrawMode(true);
        this.props.drawingFinished(feature);
        this.props.setFitBounds(bbox);

        setTimeout(() => {
          this.props.dndOverlaySetIsDragging(false);
          this.props.dndOverlaySetDndState(null);
        }, 500);
      } catch (e) {
        this.props.dndOverlaySetDndState('error');

        setTimeout(() => {
          this.props.dndOverlaySetIsDragging(false);
          this.props.dndOverlaySetDndState(null);
        }, 2000);
      }
    }

    this.dragTargets = [];
  }

  _renderUploadButton() {
    const styles = getStyles(this.props);

    return (
      <div style={styles.uploadButtonContainer}>
        <p style={styles.orLabel}>vagy</p>
        <RaisedButton
          label="Fájl kiválasztása"
          labelPosition="before"
          containerElement="label"
          primary
        >
          <input
            type="file"
            style={styles.fileInput}
            ref={ref => (this.fileUploadInputRef = ref)}
            onChange={e => this._handleDrop(e)}
          />
        </RaisedButton>
      </div>
    );
  }

  _renderOverlay() {
    const styles = getStyles(this.props);

    if (this.props.dndState) {
      const { icon: Icon, message, color } = DND_STATES[this.props.dndState];

      return (
        <div style={styles.overlay}>
          <div style={styles.overlayContent}>
            <Icon style={styles.icon} />
            <p style={{ ...styles.message, color }}>{message}</p>
            {this.props.dndState === 'awaitDrop' &&
              this.props.buttonVisible &&
              this._renderUploadButton()}
          </div>
        </div>
      );
    }
  }

  render() {
    const { children } = this.props;
    return (
      <FeatureToggle flag="hasSubscription" fallback={children}>
        <div
          onDragEnter={e => {
            this._handleDragEnter(e);
          }}
          onDragOver={e => {
            e.preventDefault();
          }}
          onDragLeave={e => {
            this._handleDragLeave(e);
          }}
          onDrop={e => {
            this._handleDrop(e);
          }}
        >
          {children}
          {this._renderOverlay()}
        </div>
      </FeatureToggle>
    );
  }
}



// WEBPACK FOOTER //
// ./components/DNDOverlay/DNDOverlay.js